import axios from 'axios';
import { getAuthHeader } from './authService';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '/api';

export interface Student {
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  photo?: string;
  schoolId?: string;
  address?: string;
  studyProgram?: string;
  gender?: 'MALE' | 'FEMALE' | 'OTHER';
  dateOfBirth?: string;
  school?: string;
  race?: string;
  emergencyContactName?: string;
  emergencyContactRelation?: string;
  emergencyContactPhone?: string;
  universityId: number;
  universityName?: string;
  providerId: number;
  providerName?: string;
  status: 'ACTIVE' | 'INACTIVE' | 'DELETED';
  emailVerified: boolean;
  phoneVerified: boolean;
  isFirstTime: boolean;
  acceptTermsOfService: boolean;
  createdAt: string;
  updatedAt: string;
  totalAssessments: number;
  completedAssessments: number;
  pendingAssessments: number;
  lastRiskLevel?: string;
  lastAssessmentDate?: string;
}

export interface StudentCreateRequest {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  schoolId?: string;
  address?: string;
  studyProgram?: string;
  gender?: 'MALE' | 'FEMALE' | 'OTHER';
  dateOfBirth?: string;
  school?: string;
  race?: string;
  emergencyContactName?: string;
  emergencyContactRelation?: string;
  emergencyContactPhone?: string;
  universityId: number;
  providerId?: number;
}

export interface StudentUpdateRequest {
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  schoolId?: string;
  address?: string;
  studyProgram?: string;
  gender?: 'MALE' | 'FEMALE' | 'OTHER';
  dateOfBirth?: string;
  school?: string;
  race?: string;
  emergencyContactName?: string;
  emergencyContactRelation?: string;
  emergencyContactPhone?: string;
  universityId?: number;
  providerId?: number;
}

export interface PaginatedResponse<T> {
  content: T[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
  first: boolean;
  last: boolean;
  empty: boolean;
}

export interface StudentFilters {
  page?: number;
  size?: number;
  search?: string;
  status?: string;
  providerId?: number;
}

export interface StudentAssessment {
  id: number;
  studentId: number;
  surveyId: number;
  assignedBy: number;
  assignedByType: string;
  dueDate?: string;
  completionStatus: 'PENDING' | 'IN_PROGRESS' | 'COMPLETED' | 'EXPIRED';
  completedAt?: string;
  notificationSent: boolean;
  reminderCount: number;
  lastReminderSent?: string;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface RiskAssessment {
  id: number;
  studentId: number;
  surveyId: number;
  riskLevel: 'GREEN' | 'YELLOW' | 'RED';
  totalScore: number;
  immediateFlag: boolean;
  flaggedResponses?: string;
  providerNotified: boolean;
  createdAt: string;
  updatedAt: string;
}

class StudentService {
  
  async getStudents(filters: StudentFilters = {}): Promise<PaginatedResponse<Student>> {
    const params = new URLSearchParams();
    
    if (filters.page !== undefined) params.append('page', filters.page.toString());
    if (filters.size !== undefined) params.append('size', filters.size.toString());
    if (filters.search) params.append('search', filters.search);
    if (filters.status) params.append('status', filters.status);
    if (filters.providerId) params.append('providerId', filters.providerId.toString());

    try {
      const response = await axios.get(`${API_BASE_URL}/students?${params.toString()}`, {
        headers: getAuthHeader()
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch students');
    }
  }

  async getStudent(id: number): Promise<Student> {
    try {
      const response = await axios.get(`${API_BASE_URL}/students/${id}`, {
        headers: getAuthHeader()
      });
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error('Student not found');
      }
      throw new Error(error.response?.data?.message || 'Failed to fetch student');
    }
  }

  async createStudent(student: StudentCreateRequest): Promise<Student> {
    try {
      const response = await axios.post(`${API_BASE_URL}/students`, student, {
        headers: getAuthHeader()
      });
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 400) {
        throw new Error(error.response?.data?.message || 'Invalid student data');
      }
      throw new Error(error.response?.data?.message || 'Failed to create student');
    }
  }

  async updateStudent(id: number, student: StudentUpdateRequest): Promise<Student> {
    try {
      const response = await axios.put(`${API_BASE_URL}/students/${id}`, student, {
        headers: getAuthHeader()
      });
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error('Student not found');
      }
      if (error.response?.status === 400) {
        throw new Error(error.response?.data?.message || 'Invalid student data');
      }
      throw new Error(error.response?.data?.message || 'Failed to update student');
    }
  }

  async deleteStudent(id: number): Promise<void> {
    try {
      await axios.delete(`${API_BASE_URL}/students/${id}`, {
        headers: getAuthHeader()
      });
    } catch (error: any) {
      if (error.response?.status === 404) {
        throw new Error('Student not found');
      }
      throw new Error(error.response?.data?.message || 'Failed to delete student');
    }
  }

  async getStudentAssessments(id: number, status?: string): Promise<StudentAssessment[]> {
    try {
      const params = status ? `?status=${status}` : '';
      const response = await axios.get(`${API_BASE_URL}/students/${id}/assessments${params}`, {
        headers: getAuthHeader()
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch student assessments');
    }
  }

  async getStudentRiskHistory(id: number, page = 0, size = 20): Promise<RiskAssessment[]> {
    try {
      const response = await axios.get(`${API_BASE_URL}/students/${id}/risk-history?page=${page}&size=${size}`, {
        headers: getAuthHeader()
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch risk history');
    }
  }

  async assignStudentToProvider(studentId: number, providerId: number): Promise<void> {
    try {
      await axios.post(`${API_BASE_URL}/students/${studentId}/assign-provider/${providerId}`, {}, {
        headers: getAuthHeader()
      });
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to assign student to provider');
    }
  }

  async getStudentsByProvider(providerId: number, page = 0, size = 20): Promise<Student[]> {
    try {
      const response = await axios.get(`${API_BASE_URL}/students/by-provider/${providerId}?page=${page}&size=${size}`, {
        headers: getAuthHeader()
      });
      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.message || 'Failed to fetch students by provider');
    }
  }

  // Utility methods
  getRiskLevelColor(riskLevel?: string): string {
    switch (riskLevel) {
      case 'RED': return '#f44336';
      case 'YELLOW': return '#ff9800';
      case 'GREEN': return '#4caf50';
      default: return '#9e9e9e';
    }
  }

  getRiskLevelIcon(riskLevel?: string): string {
    switch (riskLevel) {
      case 'RED': return '🔴';
      case 'YELLOW': return '🟡';
      case 'GREEN': return '🟢';
      default: return '⚪';
    }
  }

  getCompletionPercentage(student: Student): number {
    if (student.totalAssessments === 0) return 0;
    return Math.round((student.completedAssessments / student.totalAssessments) * 100);
  }

  formatDate(dateString?: string): string {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  }

  formatDateTime(dateString?: string): string {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString();
  }

  // Get student's assessment results
  async getMyResults(page: number = 0, size: number = 20): Promise<RiskAssessment[]> {
    try {
      const response = await axios.get(`${API_BASE_URL}/students/results`, {
        headers: getAuthHeader(),
        params: { page, size }
      });
      return response.data;
    } catch (error: any) {
      console.error('Error fetching student results:', error);
      throw new Error(error.response?.data?.message || 'Failed to fetch results');
    }
  }
}

export const studentService = new StudentService();